/**
 * Notifications via TanStack Query (docs/APP_PLAN.md §5.2, §12 M4;
 * APP_SPEC §32). This is the only place `apps/web` talks to
 * `/api/notifications/*`. The unread count is polled (no WebSocket/SSE
 * infrastructure in V1 — TECH_STACK §86, APP_SPEC §32's "in-app only").
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { Notification } from '@perplexicon/api-client';

import { apiClient } from './api-client.js';

export const NOTIFICATIONS_QUERY_KEY = ['notifications', 'list'] as const;
export const NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY = ['notifications', 'unread-count'] as const;

/** Polling interval for the navigation unread badge. */
const UNREAD_COUNT_POLL_MS = 30_000;

export function useNotifications(): UseQueryResult<Notification[]> {
  return useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => apiClient.notifications.list({ limit: 50 }),
  });
}

/** `enabled` should be `false` while signed out, so the header doesn't poll an endpoint that will just 401. */
export function useUnreadNotificationCount(enabled: boolean): UseQueryResult<number> {
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
    queryFn: () => apiClient.notifications.unreadCount(),
    enabled,
    refetchInterval: enabled ? UNREAD_COUNT_POLL_MS : false,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => apiClient.notifications.markRead(notificationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.notifications.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY });
    },
  });
}

/**
 * Deletes one notification (APP_SPEC §32). Both notification queries are
 * invalidated on success rather than being patched optimistically: a
 * failed delete must never leave a row missing from a list the server
 * still returns, and the unread count changes too whenever the deleted
 * row was unread.
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => apiClient.notifications.remove(notificationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY });
    },
  });
}

/** Deletes every one of the caller's notifications (APP_SPEC §32). */
export function useClearNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.notifications.clearAll(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY });
    },
  });
}
